import React, { useState } from "react";
import "./contact.css";
import { useFormik } from "formik";
import * as Yup from "yup";

const FORM_ENDPOINT = process.env.REACT_APP_FORM_ENDPOINT;
const CONTACT_EMAIL =
  process.env.REACT_APP_CONTACT_EMAIL || "akandetunji2@gmail.com";

function Contact() {
  // idle | sending | sent | mailto | error
  const [status, setStatus] = useState("idle");
  // Honeypot: hidden from people, bots fill it
  const [hp, setHp] = useState("");

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    subject: Yup.string().required("Subject is required"),
    message: Yup.string().required("Message is required"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, actions) => {
      // Bots fill the hidden field: pretend it worked so they move on
      if (hp) {
        setStatus("sent");
        actions.resetForm();
        return;
      }

      const payload = {
        _subject: `[Portfolio] ${values.subject.trim()}`,
        name: values.name.trim(),
        email: values.email.trim(),
        subject: values.subject.trim(),
        message: values.message.trim(),
      };

      // No endpoint configured: open the visitor's email app, pre-filled
      if (!FORM_ENDPOINT) {
        const subject = encodeURIComponent(payload._subject);
        const body = encodeURIComponent(
          `${payload.message}\n\nFrom: ${payload.name} (${payload.email})`
        );
        window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
        setStatus("mailto");
        actions.resetForm();
        return;
      }

      setStatus("sending");
      try {
        const res = await fetch(FORM_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error(String(res.status));
        setStatus("sent");
        actions.resetForm();
      } catch (err) {
        setStatus("error");
      }
    },
  });

  const isSending = status === "sending";
  const isDone = status === "sent" || status === "mailto";

  return (
    <section id="contact">
      <h5>Get In Touch</h5>
      <h2>Contact</h2>
      <div className="container contact_container">
        <div className="contact_options">
          <article className="contact_option">
            <i className="fa-regular fa-envelope icon"></i>
            <h4>Email</h4>
            <h5>{CONTACT_EMAIL}</h5>
            <a href={`mailto:${CONTACT_EMAIL}`}>Send a message</a>
          </article>
          <article className="contact_option">
            <i className="fa-brands fa-facebook-messenger icon"></i>
            <h4>Messenger</h4>
            <h5>MaXman Xman</h5>
            <a
              href="https://m.me/MaXman.Xman"
              target="_blank"
              rel="noopener noreferrer"
            >
              Send a message
            </a>
          </article>
          <article className="contact_option">
            <i className="fa-brands fa-whatsapp icon"></i>
            <h4>WhatsApp</h4>
            <a
              href="https://wa.me/2348160970232"
              target="_blank"
              rel="noopener noreferrer"
            >
              Send a message
            </a>
          </article>
        </div>

        {isDone ? (
          <div className="contact_success" role="status">
            <i className="fa-solid fa-circle-check"></i>
            <h3>
              {status === "sent" ? "Message sent!" : "Your email app should be open."}
            </h3>
            <p>
              {status === "sent"
                ? "Thanks for reaching out. I'll reply to the email address you provided as soon as I can."
                : `If nothing opened, write to ${CONTACT_EMAIL} directly.`}
            </p>
            <button
              type="button"
              className="btn"
              onClick={() => setStatus("idle")}
            >
              Send another message
            </button>
          </div>
        ) : (
          <form
            onSubmit={formik.handleSubmit}
            noValidate
            aria-busy={isSending}
          >
            <span>
              <input
                type="text"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isSending}
                className={
                  formik.errors.name && formik.touched.name ? "input-error" : ""
                }
                placeholder="Your Full Name"
              />
              {formik.touched.name && formik.errors.name && (
                <small className="error">{formik.errors.name}*</small>
              )}
            </span>
            <span>
              <input
                type="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isSending}
                className={
                  formik.errors.email && formik.touched.email
                    ? "input-error"
                    : ""
                }
                placeholder="Your Email"
              />
              {formik.touched.email && formik.errors.email && (
                <small className="error">{formik.errors.email}*</small>
              )}
            </span>
            <span>
              <input
                type="text"
                name="subject"
                value={formik.values.subject}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isSending}
                className={
                  formik.errors.subject && formik.touched.subject
                    ? "input-error"
                    : ""
                }
                placeholder="Your Message Subject Here"
              />
              {formik.touched.subject && formik.errors.subject && (
                <small className="error">{formik.errors.subject}*</small>
              )}
            </span>
            <span>
              <textarea
                name="message"
                value={formik.values.message}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={isSending}
                className={
                  formik.errors.message && formik.touched.message
                    ? "input-error"
                    : ""
                }
                placeholder="Your Message"
              ></textarea>
              {formik.touched.message && formik.errors.message && (
                <small className="error">{formik.errors.message}*</small>
              )}
            </span>

            {/* Honeypot: hidden from people and screen readers */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-9999px",
                width: 0,
                height: 0,
                overflow: "hidden",
              }}
            >
              <label>
                Leave this field empty
                <input
                  name="hp_field"
                  tabIndex={-1}
                  autoComplete="off"
                  value={hp}
                  onChange={(e) => setHp(e.target.value)}
                />
              </label>
            </div>

            {status === "error" && (
              <p role="alert" className="contact_error">
                The message did not send. Check your connection and try again,
                or email {CONTACT_EMAIL}.
              </p>
            )}

            <button
              type="submit"
              className="btn btn-primary contact_submit"
              disabled={isSending}
            >
              {isSending ? (
                <>
                  <span className="contact_spinner" aria-hidden="true"></span>
                  Sending…
                </>
              ) : (
                "Send Message"
              )}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

export default Contact;